import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * 오류 경계 — **한 화면의 오류가 관리자 전체를 하얗게 만들지 않게**.
 *
 * 왜 필요한가: 전자책 화면의 훅 순서 버그 하나로 콘솔 전체가 빈 화면이 된 적이 있다
 * (React 는 잡히지 않은 오류를 만나면 트리 전체를 언마운트한다). 운영자 입장에선
 * "관리자가 안 열려요"로 보이고 원인을 짐작할 방법이 없다.
 *
 * 여기서는 **삼키지 않는다** — 무엇이 잘못됐는지 보여주고, 다른 화면으로 갈 길을 준다.
 */
interface Props {
  children: ReactNode;
  /** 이 값이 바뀌면 오류 상태를 푼다(화면을 옮기면 다시 시도). */
  resetKey?: string;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 개발자가 원인을 볼 수 있게 콘솔에는 그대로 남긴다(조용히 삼키지 않는다).
    console.error("[admin] 화면 오류", error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="crash">
        <h2>😵 이 화면에서 문제가 생겼어요</h2>
        <p className="hint">
          다른 화면은 그대로 쓰실 수 있어요. 왼쪽에서 다른 메뉴를 골라 보세요.
          <br />
          같은 일이 계속되면 아래 내용을 개발자에게 보여주세요.
        </p>
        <pre className="crash__why">{error.message}</pre>
        <div className="crash__do">
          <button type="button" className="btn" onClick={() => this.setState({ error: null })}>
            다시 시도
          </button>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            새로고침
          </button>
        </div>
      </div>
    );
  }
}
